import { useMemo, useState } from 'react';
import { apiClient } from '../../auth-lib';
import { Button, Modal, Input, Select, StatusPill, useToast } from '../../design-system';
import { HOLDER_LABEL, HOLDER_VARIANT, fmtDate } from '../../lib/dsc';

const today = () => new Date().toISOString().slice(0, 10);

const emptyEntry = {
  holder: 'client',
  date: today(),
  toPerson: '',
  byPerson: '',
  notes: '',
};

// Custody trail for one DSC: who holds the token now, every recorded handover,
// and a form to record the next one. Keeps its own copy of the DSC (updated
// from each API response) and tells the parent to refresh via onChanged.
function DscCustodyModal({ dsc: initialDsc, onClose, onChanged }) {
  const toast = useToast();
  const [dsc, setDsc] = useState(initialDsc);
  const [entry, setEntry] = useState(emptyEntry);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setEntry((f) => ({ ...f, [k]: e.target.value }));

  // Newest first — the top row is always "where it is now".
  const history = useMemo(
    () =>
      [...(dsc.custodyLog || [])].sort(
        (a, b) => new Date(b.date) - new Date(a.date) || new Date(b.recordedAt) - new Date(a.recordedAt)
      ),
    [dsc.custodyLog]
  );

  const record = async (e) => {
    e?.preventDefault();
    setError('');
    if (!entry.toPerson.trim()) return setError('Received by (person) is required');
    setSaving(true);
    try {
      const res = await apiClient.post(`/api/dscs/${dsc._id}/custody`, {
        holder: entry.holder,
        date: entry.date || null,
        toPerson: entry.toPerson.trim(),
        byPerson: entry.byPerson.trim(),
        notes: entry.notes.trim(),
      });
      setDsc(res.data);
      setEntry({ ...emptyEntry, holder: entry.holder === 'client' ? 'firm' : 'client' });
      toast.success('Handover recorded');
      onChanged();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to record handover');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Custody — ${dsc.dscCode}`}
      footer={<Button variant="secondary" onClick={onClose}>Close</Button>}
      size="lg"
    >
      <div className="dsc-custody">
        <div className="dsc-custody-current">
          <StatusPill variant={HOLDER_VARIANT[dsc.currentHolder] || 'success'} label={HOLDER_LABEL[dsc.currentHolder] || 'With firm'} />
          {dsc.currentHolderName ? (
            <span>
              held by <strong>{dsc.currentHolderName}</strong>
              {dsc.custodySince && <> since {fmtDate(dsc.custodySince)}</>}
            </span>
          ) : (
            <span className="dsc-muted">no handover recorded yet — assumed with the firm</span>
          )}
        </div>

        <form className="dsc-custody-form" onSubmit={record}>
          {error && <div className="dsc-form-error" role="alert">{error}</div>}
          <div className="dsc-form-row">
            <Select label="Handed over to *" value={entry.holder} onChange={set('holder')}>
              <option value="client">Client</option>
              <option value="firm">Firm (returned)</option>
            </Select>
            <Input label="Date *" type="date" value={entry.date} onChange={set('date')} />
          </div>
          <div className="dsc-form-row">
            <Input label="Received by (person) *" value={entry.toPerson} onChange={set('toPerson')} placeholder="Who took the token" />
            <Input label="Handed over by" value={entry.byPerson} onChange={set('byPerson')} placeholder="Who gave it" />
          </div>
          <div className="dsc-custody-form-foot">
            <Input label="Notes" value={entry.notes} onChange={set('notes')} placeholder="Purpose, acknowledgement, courier…" />
            <Button variant="primary" onClick={record} loading={saving}>Record handover</Button>
          </div>
        </form>

        <div className="dsc-custody-history">
          <div className="dsc-custody-history-head">
            <h4>History</h4>
            <span className="dsc-custody-immutable">Immutable — correct a mistake by recording the correcting handover.</span>
          </div>
          {history.length === 0 ? (
            <p className="dsc-muted">No handovers recorded.</p>
          ) : (
            <ul>
              {history.map((h) => (
                <li key={h._id} className="dsc-custody-entry">
                  <div className="dsc-custody-entry-main">
                    <StatusPill variant={HOLDER_VARIANT[h.holder]} label={HOLDER_LABEL[h.holder]} />
                    <span className="dsc-custody-entry-date">{fmtDate(h.date)}</span>
                    <span>
                      received by <strong>{h.toPerson}</strong>
                      {h.byPerson && <>, handed by <strong>{h.byPerson}</strong></>}
                    </span>
                  </div>
                  {h.notes && <div className="dsc-custody-entry-notes">{h.notes}</div>}
                  <div className="dsc-custody-entry-meta">
                    recorded by {h.recordedByName || '—'} on {fmtDate(h.recordedAt)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default DscCustodyModal;
